import { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Minus,
  Plus,
  Trash2,
  Check,
  Share2,
  Truck,
  RefreshCw,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Grid2x2,
  MessageCircle,
} from "lucide-react";
import { useFetchProductBySlugQuery, useFetchRandomProductsQuery } from "../../stores/api/pages.api";
import { UserLayout } from "./components/layout/UserLayout";
import Container from "./components/Container";
import PageHero from "./components/PageHero";
import SelectBox from "./components/SelectBox";
import Slider from "./components/Slider";
import ProductCard from "./components/cards/ProductCard";
import Text from "./components/Text";
import SimpleTable from "./components/SimpleTable";
import Img from "./components/Img";
import ReviewSection from "./components/ReviewSection";
import useTrackView from "../../hooks/useTrackView";
import { buildWhatsappUrl, getImg } from "../../Components/Utils";
import { ROUTE_PATHS } from "../../constants/routeConstants";


const CARE_ICONS = ["⊙", "⊡", "⊘", "⊛", "⊟"];

const ProductSingle = () => {
  const { productSlug } = useParams();
  const { data: product, isLoading } = useFetchProductBySlugQuery(productSlug);
  const { data: relatedRaw = [] } = useFetchRandomProductsQuery(productSlug);
  useTrackView("product", product?.id);
  const related = relatedRaw.map((p) => ({
    ...p,
    title: p.name,
    img: p.image,
    hoverImg: p.image,
  }));
  const whatsappNumber = process.env.REACT_APP_WHATSAPP_NUMBER;

  const [activeImg, setActiveImg] = useState(0);

  const imgRef = useRef(null);
  const ZOOM = 2.5;
  const LENS_W = 160;
  const LENS_H = 160;
  const [magnifier, setMagnifier] = useState({
    visible: false,
    lensX: 0,
    lensY: 0,
    bgX: 0,
    bgY: 0,
    bgW: 0,
    bgH: 0,
  });

  const handleMouseMove = (e) => {
    const el = imgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const lensX = Math.min(Math.max(x - LENS_W / 2, 0), rect.width - LENS_W);
    const lensY = Math.min(Math.max(y - LENS_H / 2, 0), rect.height - LENS_H);
    const bgW = rect.width * ZOOM;
    const bgH = rect.height * ZOOM;
    const bgX = -(lensX * ZOOM);
    const bgY = -(lensY * ZOOM);
    setMagnifier({ visible: true, lensX, lensY, bgX, bgY, bgW, bgH });
  };

  const prev = () =>
    product && setActiveImg(
      (i) => (i - 1 + images.length) % images.length,
    );
  const next = () => product && setActiveImg((i) => (i + 1) % images.length);

  const [active, setActive] = useState(0);
  const [displayed, setDisplayed] = useState(0);
  const [phase, setPhase] = useState("in");
  const [tabHeight, setTabHeight] = useState("auto");
  const contentRef = useRef(null);

  const switchTab = (i) => {
    if (i === active) return;
    if (contentRef.current)
      setTabHeight(contentRef.current.scrollHeight + "px");
    setPhase("out");
    setTimeout(() => {
      setDisplayed(i);
      setActive(i);
      setPhase("in");
      requestAnimationFrame(() => {
        if (contentRef.current) {
          setTabHeight(contentRef.current.scrollHeight + "px");
          setTimeout(() => setTabHeight("auto"), 300);
        }
      });
    }, 250);
  };

  if (isLoading) {
    return (
      <UserLayout>
        <div className="max-w-[1440px] mx-auto px-6 lg:px-16 py-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div className="animate-pulse bg-gray-200 aspect-[3/4] rounded-sm" />
          <div className="space-y-4">
            <div className="animate-pulse bg-gray-200 h-8 w-3/4 rounded" />
            <div className="animate-pulse bg-gray-200 h-4 w-1/4 rounded" />
            <div className="animate-pulse bg-gray-200 h-4 w-1/2 rounded" />
            <div className="animate-pulse bg-gray-200 h-20 w-full rounded" />
            <div className="animate-pulse bg-gray-200 h-12 w-full rounded" />
            <div className="animate-pulse bg-gray-200 h-12 w-full rounded" />
          </div>
        </div>
      </UserLayout>
    );
  }

  if (!product) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-gray-400 text-sm">Product not found.</p>
        </div>
      </UserLayout>
    );
  }

  const title = product.name ?? product.title ?? "";
  const images = product.images?.length
    ? product.images
    : [product.image].filter(Boolean);

  const tabs = [
    {
      label: "Description",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            {product.feat?.slice(0, 2).map((section) => (
              <div key={section.title} className="mb-6">
                <Text is="p" className="text-xs font-semibold text-gray-900 mb-3">
                  {section.title}
                </Text>
                <ul className="space-y-2">
                  {section.items.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div>
            {product.feat?.slice(2, 3).map((section) => (
              <div key={section.title}>
                <Text is="p" className="text-xs font-semibold text-gray-900 mb-4">
                  {section.title}
                </Text>
                <ul className="space-y-4">
                  {section.items.map((label, i) => (
                    <li key={label} className="flex items-center gap-3 text-sm text-gray-600">
                      <span className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-base flex-shrink-0">
                        {CARE_ICONS[i] || "⊙"}
                      </span>
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      label: "Review",
      content: <ReviewSection productId={product.id} />,
    },
    {
      label: "Shipping",
      content: (
        <div className="space-y-4">
          <Text is="h3" className="text-sm font-semibold text-gray-900">
            {product.shipping_policy?.title ?? "Shipping Policy"}
          </Text>
          {(product.shipping_policy?.content ?? []).map((paragraph, i) => {
            const email = "marketing@company.com";
            const parts = paragraph.split(email);
            return (
              <Text key={i} is="p" className="text-sm text-gray-600 leading-relaxed">
                {parts.map((part, j) =>
                  j < parts.length - 1 ? (
                    <span key={j}>
                      {part}
                      <Text
                        is="a"
                        href={`mailto:${email}`}
                        className="text-gray-900 underline hover:text-blue-500 transition-colors"
                      >
                        {email}
                      </Text>
                    </span>
                  ) : (
                    part
                  ),
                )}
              </Text>
            );
          })}
        </div>
      ),
    },
    {
      label: "Return Policies",
      content: (
        <Text is="p" className="text-sm text-gray-500">
          {product.return_policy}
        </Text>
      ),
    },
  ];

  return (
    <UserLayout>
      <PageHero
        eyebrow={product.badge}
        title={title}
        crumbs={[
          { label: "Products", href: ROUTE_PATHS.PRODUCTS },
          { label: title },
        ]}
      />

      {/* Product nav row */}
      <Container
        variant="narrow"
        className="px-4 lg:px-16 py-3 flex items-center justify-end gap-3 border-b border-gray-100"
      >
        <button
          onClick={prev}
          className="w-7 h-7 border border-gray-200 flex items-center justify-center hover:border-gray-900 transition-colors"
          aria-label="Previous product"
        >
          <ChevronLeft size={13} />
        </button>
        <Grid2x2 size={15} className="text-gray-400" />
        <button
          onClick={next}
          className="w-7 h-7 border border-gray-200 flex items-center justify-center hover:border-gray-900 transition-colors"
          aria-label="Next product"
        >
          <ChevronRight size={13} />
        </button>
      </Container>

      <Container
        variant="narrow"
        className="px-6 lg:px-16 py-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 relative"
      >
        {/* ── Left: Gallery ── */}
        <div className="flex gap-3">
          {/* Thumbnails */}
          <div className="hidden sm:flex flex-col gap-2 w-[80px] flex-shrink-0 group/thumbs">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`aspect-square overflow-hidden rounded-sm border-2 transition-all ${magnifier.visible ? "opacity-50" : "opacity-100"} group-hover/thumbs:opacity-50 ${activeImg === i ? "border-gray-900 !opacity-100" : "border-transparent"}`}
              >
                <Img
                  src={getImg(img)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>

          {/* Main image */}
          <div
            ref={imgRef}
            className="relative flex-1 aspect-[3/4] overflow-hidden rounded-sm bg-gray-50 group cursor-crosshair"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setMagnifier((m) => ({ ...m, visible: false }))}
          >
            <Img
              src={getImg(images[activeImg])}
              alt={title}
              className="w-full h-full object-cover"
            />
            {magnifier.visible && (
              <div
                className="absolute pointer-events-none border border-gray-300 bg-white/20"
                style={{
                  width: LENS_W,
                  height: LENS_H,
                  left: magnifier.lensX,
                  top: magnifier.lensY,
                }}
              />
            )}
            <button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white rounded-full shadow flex items-center justify-center hover:bg-gray-900 hover:text-white"
            >
              <ChevronLeft size={15} />
            </button>
            <button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white rounded-full shadow flex items-center justify-center hover:bg-gray-900 hover:text-white"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>

        {/* ── Zoom preview panel ── */}
        {magnifier.visible && (
          <div
            className="hidden lg:block absolute top-10 left-1/2 w-[500px] aspect-square rounded-md overflow-hidden bg-gray-50 pointer-events-none z-20"
            style={{
              backgroundImage: `url(${getImg(images[activeImg])})`,
              backgroundSize: `${magnifier.bgW}px ${magnifier.bgH}px`,
              backgroundPosition: `${magnifier.bgX}px ${magnifier.bgY}px`,
              backgroundRepeat: "no-repeat",
            }}
          />
        )}

        {/* ── Right: Info ── */}
        <div
          className={`flex flex-col gap-5 transition-opacity duration-150 ${magnifier.visible ? "lg:opacity-50" : "lg:opacity-100"}`}
        >
          {/* Title + badge */}
          <div>
            <Text is="h1" className="text-2xl font-bold text-gray-900 mb-2">
              {title}
            </Text>
            <div className="flex items-center gap-3">
              <Text
                is="span"
                className="text-xs font-semibold bg-gray-900 text-white px-2.5 py-1 rounded"
              >
                {product.badge}
              </Text>
              <Text is="p" className="text-xs text-gray-500">
                {product.alert}
              </Text>
            </div>
          </div>

          {/* Viewers */}
          <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-lg w-fit">
            <Text
              is="span"
              className="w-5 h-5 bg-gray-900 text-white rounded flex items-center justify-center font-bold text-[10px]"
            >
              {product.viewers}
            </Text>
            <Text is="span">People are viewing this right now</Text>
          </div>

          {/* Description */}
          <div className="flex-1">
            <Text is="p" className="text-sm text-gray-600 leading-relaxed">
              {product.description}
            </Text>
          </div>

          {/* WhatsApp CTA */}
          <Text
            is="a"
            href={buildWhatsappUrl(product)}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-[#25D366] hover:bg-[#1ebe5d] transition-colors text-white font-semibold py-3.5 rounded flex items-center justify-center gap-2 shadow"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Order via WhatsApp
          </Text>

          {/* Action links */}
          <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
            <button className="flex items-center gap-1.5 hover:text-gray-900 transition-colors">
              <span className="w-5 h-5 rounded-full bg-gradient-to-br from-yellow-400 via-blue-400 to-blue-500 flex-shrink-0" />
              <Text is="span">Compare color</Text>
            </button>
            <button className="flex items-center gap-1.5 hover:text-gray-900 transition-colors">
              <MessageCircle size={13} /> <Text is="span">Ask a question</Text>
            </button>
            <button className="flex items-center gap-1.5 hover:text-gray-900 transition-colors">
              <Truck size={13} /> <Text is="span">Delivery &amp; Return</Text>
            </button>
            <button className="flex items-center gap-1.5 hover:text-gray-900 transition-colors">
              <Share2 size={13} /> <Text is="span">Share</Text>
            </button>
          </div>

          {/* Info cards */}
          {(product.delivery_estimate || product.return_policy_short) && (
            <div className="grid grid-cols-2 gap-3">
              {product.delivery_estimate && (
                <div className="border border-gray-100 rounded-lg p-4 text-center">
                  <Truck size={24} className="mx-auto mb-2 text-gray-400" />
                  <Text is="p" className="text-xs text-gray-500 leading-relaxed">
                    {product.delivery_estimate}
                  </Text>
                </div>
              )}
              {product.return_policy_short && (
                <div className="border border-gray-100 rounded-lg p-4 text-center">
                  <RefreshCw size={24} className="mx-auto mb-2 text-gray-400" />
                  <Text is="p" className="text-xs text-gray-500 leading-relaxed">
                    {product.return_policy_short}
                  </Text>
                </div>
              )}
            </div>
          )}

          {/* Guarantee */}
          <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100 text-xs text-gray-500">
            <ShieldCheck size={14} className="text-gray-400" />
            <Text is="span">Guarantee Safe Checkout</Text>
          </div>
        </div>
      </Container>

      {/* ── Product Tabs ── */}
      <Container variant="narrow">
        <div className="border p-7 ">
          {/* Tab bar */}
          <div className="flex border-b border-gray-200 mb-8 overflow-x-auto max-w-full">
            {tabs.map((tab, i) => (
              <button
                key={tab.label}
                onClick={() => switchTab(i)}
                className="relative mr-8 pb-3 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
              >
                {active === i && (
                  <span className="absolute bottom-0 left-0 h-0.5 bg-gray-900 animate-underline" />
                )}
                <Text is="span" className={active === i ? "text-gray-900" : ""}>
                  {tab.label}
                </Text>
              </button>
            ))}
          </div>
          {/* Tab content */}
          <div
            style={{
              height: tabHeight,
              transition: "height 200ms ease-out",
              overflow: "hidden",
            }}
          >
            <div
              ref={contentRef}
              style={{
                opacity: phase === "out" ? 0 : 1,
                transform:
                  phase === "out" ? "translateY(8px)" : "translateY(0)",
                transition: "opacity 250ms ease-out, transform 250ms ease-out",
              }}
            >
              {tabs[displayed].content}
            </div>
          </div>
        </div>
      </Container>

      {/* ── People Also Bought ── */}
      {related.length > 0 && (
        <Container variant="narrow" className="px-6 lg:px-16 py-12">
          <Text
            is="h2"
            className="text-2xl font-medium text-gray-900 text-center mb-8"
          >
            People Also Bought
          </Text>
          <Slider
            options={{
              type: "loop",
              perPage: 4,
              perMove: 1,
              gap: "1rem",
              pagination: false,
              arrows: true,
              breakpoints: { 1024: { perPage: 3 }, 640: { perPage: 2 } },
            }}
          >
            {({ SplideSlide }) =>
              related.map((item, i) => (
                <SplideSlide key={i}>
                  <ProductCard product={item} />
                </SplideSlide>
              ))
            }
          </Slider>
        </Container>
      )}
    </UserLayout>
  );
};
export default ProductSingle;
